import { ContentChild, ContentChildren, Directive, ElementRef, forwardRef, Inject, QueryList } from '@angular/core';
import { DOCUMENT } from '@angular/common';

export abstract class KeyboardNavigationDirectiveBase {
  abstract onKeyDown(event: KeyboardEvent): void;
}

@Directive({
  selector: '[bbKeyboardNavigationItem]',
})
export class KeyboardNavigationItemDirective {
  constructor(
    @Inject(forwardRef(() => KeyboardNavigationDirective)) public navigator: KeyboardNavigationDirectiveBase,
    public elementRef: ElementRef<HTMLElement>,
  ) {}
}

@Directive({
  selector: '[bbKeyboardNavigationAnchor]',
  // tslint:disable-next-line:no-host-metadata-property
  host: {
    '(keydown.ArrowLeft)': 'navigator.onKeyDown($event)',
    '(keydown.ArrowRight)': 'navigator.onKeyDown($event)',
    '(keydown.ArrowDown)': 'navigator.onKeyDown($event)',
    '(keydown.Tab)': 'navigator.onKeyDown($event)',
  },
})
export class KeyboardNavigationAnchorDirective {
  readonly nativeElement = this.elementRef.nativeElement;

  constructor(
    @Inject(forwardRef(() => KeyboardNavigationDirective)) public navigator: KeyboardNavigationDirectiveBase,
    public elementRef: ElementRef<HTMLElement>,
  ) {}
}

@Directive({
  selector: '[bbKeyboardNavigationList]',
  // tslint:disable-next-line:no-host-metadata-property
  host: {
    '(keydown.ArrowUp)': 'navigator.onKeyDown($event)',
    '(keydown.ArrowDown)': 'navigator.onKeyDown($event)',
    '(keydown.Shift)': 'navigator.onKeyDown($event)',
    '(keydown.Tab)': 'navigator.onKeyDown($event)',
    '(keydown.Shift.Tab)': 'navigator.onKeyDown($event)',
  },
})
export class KeyboardNavigationListDirective {
  readonly nativeElement = this.elementRef.nativeElement;
  @ContentChildren(KeyboardNavigationItemDirective, { descendants: true })
  items!: QueryList<KeyboardNavigationItemDirective>;

  constructor(
    @Inject(forwardRef(() => KeyboardNavigationDirective)) public navigator: KeyboardNavigationDirectiveBase,
    public elementRef: ElementRef<HTMLElement>,
  ) {}
}

@Directive({
  selector: '[bbKeyboardNavigation]',
})
export class KeyboardNavigationDirective implements KeyboardNavigationDirectiveBase {
  @ContentChild(KeyboardNavigationListDirective, { static: false }) private list!: KeyboardNavigationListDirective;
  @ContentChildren(KeyboardNavigationAnchorDirective, { descendants: true })
  private anchorList!: QueryList<KeyboardNavigationAnchorDirective>;
  private _document!: Document;

  private static resetItem(position: number, itemElements: HTMLElement[]) {
    if (position === -1) return;
    itemElements[position].removeAttribute('tabindex');
  }

  private static resetTabItem(position: number, itemElements: HTMLElement[]) {
    itemElements[position].setAttribute('tabindex', '-1');
  }

  private static setItemActive(position: number, itemElements: HTMLElement[]) {
    itemElements[position].setAttribute('tabindex', '0');
    itemElements[position].focus();
  }

  private static setPositionUp(position: number): number {
    return Math.max(position - 1, 0);
  }

  private static setPositionDown(position: number, itemsLength: number) {
    return Math.min(position + 1, itemsLength - 1);
  }

  private isEventFromAnchor($event: KeyboardEvent): boolean {
    return !!this.anchorList?.find(el => {
      return el.nativeElement.contains($event.target as HTMLElement);
    });
  }

  private getListElements(): HTMLElement[] {
    return this.list?.items.map(item => item.elementRef.nativeElement);
  }

  private getListAnchor(): HTMLElement[] {
    return this.anchorList?.map(item => item.elementRef.nativeElement);
  }

  constructor(@Inject(DOCUMENT) private readonly document: any) {
    this._document = this.document as Document;
  }

  onKeyDown(event: KeyboardEvent) {
    const key = event.key;
    const itemElements = this.getListElements();
    const anchorElements = this.getListAnchor();
    const isEventFromAnchor = this.isEventFromAnchor(event);
    const horizontalNav = new Set(['ArrowRight', 'ArrowLeft']);

    let itemPosition = -1;
    let anchorPosition = 0;
    let itemElement: HTMLElement | null = null;

    if (key === 'ArrowDown' || key === 'Tab') {
      if (isEventFromAnchor && itemElements?.length) {
        KeyboardNavigationDirective.setItemActive(0, itemElements);
        event.preventDefault();
      }

      if (key === 'Tab') {
        return;
      }
    }

    if (!isEventFromAnchor && itemElements?.length) {
      itemElements.forEach((item, index) => {
        if (item.contains(event.target as HTMLElement)) {
          itemElement = item;
        }

        if (item === this._document.activeElement) {
          itemPosition = index;
        }
      });
    }

    if (isEventFromAnchor && anchorElements.length > 1 && horizontalNav.has(key)) {
      anchorElements.forEach((item, index) => {
        if (item === this._document.activeElement) {
          anchorPosition = index;
        }
      });

      switch (key) {
        case 'ArrowRight':
          KeyboardNavigationDirective.resetTabItem(anchorPosition, anchorElements);
          anchorPosition = KeyboardNavigationDirective.setPositionDown(anchorPosition, anchorElements.length);
          break;
        case 'ArrowLeft':
          KeyboardNavigationDirective.resetTabItem(anchorPosition, anchorElements);
          anchorPosition = KeyboardNavigationDirective.setPositionUp(anchorPosition);
          break;
      }

      KeyboardNavigationDirective.setItemActive(anchorPosition, anchorElements);
      return;
    }

    if (!isEventFromAnchor && itemElement) {
      switch (key) {
        case 'ArrowDown':
          KeyboardNavigationDirective.resetItem(itemPosition, itemElements);
          itemPosition = KeyboardNavigationDirective.setPositionDown(itemPosition, itemElements.length);
          break;
        case 'ArrowUp':
          KeyboardNavigationDirective.resetItem(itemPosition, itemElements);
          itemPosition = KeyboardNavigationDirective.setPositionUp(itemPosition);
          break;
      }
      KeyboardNavigationDirective.setItemActive(itemPosition, itemElements);

      event.preventDefault();
    }
  }
}
